import Layout from '@/components/layout';
import { Pages } from '@/lib/seoData';
import { NextSSRPage, withServerSideProps } from 'hoc/withServerSideProps';

const PrivacyPolicyPage: NextSSRPage = (props) => {
  return (
    <Layout {...props}>
      <div className="flex flex-col flex-grow h-full justify-center mx-auto px-4 py-8 lg:px-[100px] lg:py-[60px]">
        <h1 className="mb-14 leading-[3.5rem]">Privacy Policy</h1>
        <div className="prose min-w-full">
          <p className="uppercase">1. Introduction</p>
          <p>
            The purpose of this Privacy Policy is to allow you (the “User“) to
            understand how we, Daily Polished Inc. (“Daily Polished“), collect and
            use the personal information you provide to us, including through our
            website (the “Site“), and other channels, both offline and online,
            that reference or link to this Privacy Policy.
          </p>
          <p>
            This Privacy Policy applies to information collected by Daily Polished
            and does not apply to information collected by any third-party sites.
          </p>
          <p>
            By using the Sites, you agree to the collection, use, and disclosure
            of your information as described in this Privacy Policy. If you do not
            agree, please do not access or use the Site.
          </p>
          <p>
            <span className="font-bold">
              California Notice of Collection of Personal Information
            </span>
            : Daily Polished collects and uses the personal information described
            in Section 2, Information We Collect and How We Use It, for the
            purposes described in Section 2. California residents: to learn more
            about Daily Polished’s collection and use of personal information,
            including how to opt out of sales or sharing of personal information,
            please scroll down to Section 14, Your California Privacy Rights.
          </p>
          <p className="uppercase">2. Information We Collect and How We Use It</p>
          <p>
            <span className="font-bold">Information You Share</span>: Daily
            Polished collects information from you when you choose to share it
            with us, called “Information You Share“. This may include when you use
            our Sites to join our membership program, make a purchase, request
            customer support, engage in other transactions or activities. The
            information we collect may include your name, your username, your
            e-mail address, your mailing address, your telephone number, your date
            of birth, and your credit card or other payment information. If you
            contact our customer support team (by email, chat, or telephone) then
            we will also collect the content of your communications with us. We
            will use this information to resolve your inquiries and to ensure the
            quality of our customer support.
          </p>
          <p>
            We use Information you Share for business purposes, including to enter
            into a transaction agreement with you, to provide the services
            requested and fulfill your orders, to process payments and prevent
            transactional fraud, to respond to inquiries, and to validate
            membership activity. When you order products through our Site, your
            name, shipping address, and email address will be shared with our
            Brand Partners, whose products you are purchasing so that they may
            fulfill your orders, and to communicate with you about your orders. In
            accordance with our Membership Agreement, our Brand Partners may use
            this information to contact you for marketing and advertising
            purposes.
          </p>
          <p>
            We may also use Information You Share for commercial purposes,
            including to contact you or send you notifications about products and
            services that you have ordered, to send you notifications about
            products and services that we otherwise think may be of interest to
            you, or to send you promotional items, gifts, or account credits from
            time to time, in our sole discretion. On occasion, we may have other
            reasons for processing your personal information, but these purposes
            are compatible with the purposes for which you provided your
            information to us and our legitimate business interests.
          </p>
          <p>
            <span className="font-bold">
              Information We Collect Automatically
            </span>
            : When a User visits the Sites, our servers and third party analytics
            providers automatically record information regarding the User’s
            browser type, browser language, platform type, IP address, geolocation
            web request, unique identifier, device identifier, app usage data,
            number of clicks, domain names, the amount of time spent on particular
            pages, the date and time of use of Sites and interactions with the
            Sites, clickstream data (e.g., about the pages you view, links you
            click and date and time stamps for your activities on our Sites),
            phone model, phone operating system, and mobile carrier information,
            collectively called “Information We Collect Automatically“. We store
            Information We Collect Automatically through the use of cookies,
            pixels, or other similar technologies.
          </p>
          <p>
            Daily Polished uses Information We Collect Automatically for the
            following business and commercial purposes:
          </p>
          <ul>
            <li className="mb-2">
              (a) to diagnose and prevent service or technology problems;
            </li>
            <li className="mb-2">
              (b) to help us provide you with customized content and promotions;
            </li>
            <li className="mb-2">
              (c) to prepare geographical or other statistical data to help us
              better serve our Users;
            </li>
            <li className="mb-2">
              (d) to create new features, promotions, and services in connection
              with the Sites;
            </li>
            <li className="mb-2">
              (e) to measure the effectiveness of our advertising campaigns;
            </li>
            <li className="mb-2">(f) to monitor the use of the Sites;</li>
            <li className="mb-2">
              (g) to confirm that Users have visited previously;
            </li>
            <li className="mb-2">
              (h) to keep track of the status of your shopping cart; and
            </li>
            <li className="mb-2">(i) to perform other functions on the Sites.</li>
          </ul>
          <p>
            You can set your browser to accept all cookies, reject all cookies, or
            notify you when a cookie is set. However, if you set your browser to
            disable all cookies, some features of the Sites may not function
            properly. Please see our Cookies & Tracking Technologies Policy for
            more information on your choices regarding cookies.
          </p>
          <p className="uppercase">3. Disclosure of your Information</p>
          <p>
            Daily Polished may provide your information to third-party service
            providers that we engage to assist us with the operation of the Sites
            and the provision of products and services. Such access is provided
            with the understanding that these parties will use the information for
            these limited purposes and in a manner consistent with our Privacy
            Policy.
          </p>
          <p>
            Unless otherwise disclosed at the time of collection of information,
            Daily Polished does not share your information with third parties
            other than our Brand Partners for their own direct marketing purposes.
          </p>
          <p>
            Daily Polished may also provide your information to service-providers
            who provide marketing insights, such as lookalike models or other
            statistical research. Daily Polished may also provide general
            demographic, aggregated, or deidentified information about Users and
            their preferences to advertisers and other existing or prospective
            business partners.
          </p>
          <p>
            In addition, Daily Polished reserves the right to share information
            with authorized third parties if:
          </p>
          <ul>
            <li className="mb-2">
              (a) we believe we are required to do so in accordance with law,
              regulation or to respond to a subpoena, court order, or other lawful
              request by a public authority, including to meet national security
              or law enforcement requirements;
            </li>
            <li className="mb-2">
              (b) we believe that such disclosure is necessary or appropriate to
              enforce our Terms of Use and Sale;
            </li>
            <li className="mb-2">
              (c) we believe that such disclosure is necessary or appropriate to
              take precautions against liability;
            </li>
            <li className="mb-2">
              (d) we believe that such disclosure is necessary or appropriate to
              investigate and defend ourselves against any third-party claims or
              allegations;
            </li>
            <li className="mb-2">
              (e) we believe that such disclosure is necessary or appropriate to
              protect the security or integrity of the Sites or services;
            </li>
            <li className="mb-2">
              (f) we believe that such disclosure is necessary or appropriate to
              protect the rights, safety or property of Daily Polished, our Users
              or others; or
            </li>
            <li className="mb-2">
              (g) Daily Polished becomes involved in a merger, acquisition, or
              other transaction resulting in a change of control of Daily Polished
              or a sale of substantially all of the assets of the business or of a
              particular product line or division of the business, or negotiations
              for any such transaction, your information may be transferred in
              connection with the transaction or potential transaction and may
              become subject to the privacy policy of another entity.
            </li>
          </ul>
          <p>
            We may further share your information as otherwise disclosed to you at
            the time of collection.
          </p>
          <p className="uppercase">4. Advertising Choices</p>
          <p>
            Daily Polished or third parties on our behalf may use cookies or
            similar technologies to recognize your computer and collect
            information about your activity over time on our Sites and other
            online services to facilitate delivery of targeted advertisements that
            may be relevant to you.
          </p>
          <p>
            Even if you opt out, you may still receive advertising from us that is
            not customized based on your interests or preferences, or that is from
            other parties that are not participants in the DAA or NAI.
          </p>
          <p className="uppercase">5. Security</p>
          <p>
            We take measures to help keep information secure and to help prevent
            it from becoming disclosed. Even though we follow procedures to try to
            protect the information in our possession, no security system is
            perfect so we cannot guarantee, and you should not expect, that your
            information will be secure in all circumstances. When you enter you
            payment information on our forms, your information is stored directly
            on our payment processing partner’s system and the transmission of
            that information is encrypted. We do not store or have access to that
            information.
          </p>
          <p className="uppercase">6. Data Rights</p>
          <p>
            We will make reasonable efforts to ensure Information You Share is
            accurate and complete and we will update or correct your information
            as needed when notified by you. In addition, you can manage your
            account information through your Daily Polished account. If you would
            like to request to verify your personal information, identify any
            inaccuracy in your personal information, or change your personal
            information in any way or if you have a legal right to and would like
            to request access to your personal information, please email us at
            Care@DailyPolished.com.
          </p>
          <p>
            <span className="font-bold">California Residents </span>: see Section
            13, Your California Privacy Rights below.
          </p>
          <p className="uppercase">7. Retention</p>
          <p>
            We will retain information we collect in active files or systems as
            long as needed to meet the purposes for which it was collected, to
            fulfill the other purposes outlined herein, or as necessary to satisfy
            legal, accounting, or reporting requirements.
          </p>
          <p className="uppercase">8. Other Sites</p>
          <p>
            As noted above, this Privacy Policy only applies to our Sites. Our
            Sites may include links to other sites, but Daily Polished does not
            control the privacy practices of other sites. Daily Polished is not
            responsible for the privacy practices of other websites.
          </p>
          <p className="uppercase">9. Changes to this Policy</p>
          <p>
            Daily Polished may change its Privacy Policy from time to time by
            posting such revisions through the Sites or by emailing a link to the
            revised Privacy Policy to the email address listed on your account.
            Revisions will become effective on the effective date indicated at the
            top of the page. Your continued use of the Sites following the posting
            of changes to these terms means you accept these changes.
          </p>
          <p className="uppercase">10. How We Communicate with You</p>
          <p>
            If you have any questions about our Privacy Policy, you can contact us
            by emailing us at privacy@Daily Polished.com. If we need, or are
            required, to contact you concerning any event that involves your
            information, you agree that we may do so by email, telephone, or mail.
          </p>
          <p className="uppercase">11. Do Not Track Signals</p>
          <p>
            Some web browsers and devices allow you to broadcast a preference that
            your activities online not be “tracked“. At this time, our Sites do
            not take action in response to “do not track“ signals.
          </p>
          <p className="uppercase">12. Children</p>
          <p>
            The Service is not intended for use by children under 13 years of age.
            We do not knowingly collect or solicit information from children under
            the age of 13.
          </p>
          <p>
            If you are a parent or guardian of a child under the age of thirteen
            (13) and believe he or she has disclosed personal information to us,
            please contact us at privacy@Daily Polished.com.
          </p>
          <p>
            If we become aware that we have collected personal information of a
            child under 13 years of age through the Sites or services, we take
            steps to remove that information and terminate the child’s account.
          </p>
          <p className="uppercase">13. Your California Privacy Rights</p>
          <p className=" uppercase">13.1 Scope.</p>
          <p>
            {' '}
            This section applies only to California residents. For purposes of
            this section, “Personal Information“ has the meaning given in the CCPA
            but does not include information exempted from the scope of the CCPA.
            In some cases we may provide a different privacy notice to certain
            categories of California residents, such as job applicants, in which
            case that notice will apply instead of this section.
          </p>
          <p className="uppercase"> 13.2 Your rights.</p>
          <p>
            {' '}
            As a California resident, you have the rights listed below. However,
            these rights are not absolute, and in certain cases we may decline
            your request as permitted by law.
          </p>
          <ul>
            <li className="mb-2">
              (a) Access. You can request, up to two times each year, that we
              disclose the categories and/or specific pieces of Personal
              Information that we collect, use, disclose, and may sell.
            </li>
            <li className="mb-2">
              (b) Deletion. You can ask us to delete the Personal Information that
              we have collected from you, subject to certain exceptions such as to
              complete a transaction for you, to exercise our rights, or to comply
              with a legal obligation.
            </li>
            <li className="mb-2">
              (c) Correction. You can ask us to correct inaccurate Personal
              Information that we collect about you.
            </li>
            <li className="mb-2">
              (d) Opt-out of sales and sharing. If we sell or share your Personal
              Information, you can opt-out of any sale or sharing of your Personal
              Information.
            </li>
            <li className="mb-2">
              (e) Limit the Use of Sensitive Personal Information. Californian’s
              have the right to limit a business’s use or disclosure of sensitive
              personal information for non-permissible purposes. However, we do
              not use or disclose sensitive personal information for
              non-permissible purposes under the CCPA.
            </li>
            <li className="mb-2">
              (f) Nondiscrimination.You are entitled to exercise the rights
              described above free from retaliation or discriminatory treatment as
              prohibited by the CCPA.
            </li>
          </ul>
          <p className="uppercase">
            13.3 Right to access, correction and deletion.
          </p>
          <p>
            You may submit a request to exercise your right to access, correction
            or deletion of your Personal Information by email to{' '}
            <span className="font-bold">
              <a href="mailto:Care@DailyPolished.com">Care@DailyPolished.com.</a>
            </span>
            . We will need to verify your identity to process your request and
            reserve the right to confirm your California residency. To verify your
            identity, we may require you to verify your email address, to log into
            your service account if you have one, or to provide government
            identification or other information. Your authorized agent may make a
            request on your behalf upon our verification of the agent’s identity
            and our receipt of a copy of a valid power of attorney given to your
            authorized agent pursuant to California Probate Code Sections
            4000-4465. If you have not provided your agent with such a power of
            attorney, you must provide your agent with written and signed
            permission to exercise your CCPA rights on your behalf, provide the
            information we request to verify your identity, and provide us with
            written confirmation that you have given the authorized agent
            permission to submit the request.
          </p>
          <p className="uppercase">
            13. 4 Notice of right to opt-out of the “sale” or “sharing” of your
            Personal Information.
          </p>
          <p>
            {' '}
            Like many companies, we use online advertising services that employ
            cookies and similar technologies to collect information about your
            device and online activity on our sites and other online services.
            They use this information to try to tailor the ads you see online to
            your interests. Our use of some of these services may be classified
            under California law as a “sale” or “sharing” of your Personal
            Information to the advertising partners because they collect
            information from our users to help them serve ads more likely to
            interest users on behalf of us and their other clients. You can
            opt-out of the use of this data for interest-based advertising
            purposes by navigating to the homepage, clicking Do Not Sell or Share
            My Personal Information in the footer and setting your preferences. We
            do not have actual knowledge that we sell or share personal
            information of consumers under 13 years of age.
          </p>
          <p className="uppercase">
            13.5 Personal information that we collect, use and disclose
          </p>
          <p>
            The list below summarizes the Personal Information we collect by
            reference to the categories of Personal Information specified in the
            CCPA. Information you voluntarily provide to us, such as in free-form
            webforms, may contain other categories of personal information not
            described below.
          </p>
          <ul>
            <li className="mb-2">
              (a){' '}
              <span className="font-bold">Personal Information Collected:</span>{' '}
              Identifiers/biographical information, characteristics of protected
              classifications under California or federal law (i.e., age),
              commercial information, payment card information, internet or other
              electronic network activity information, and geolocation
              information. The sources of this information are the consumer,
              analytics providers, advertising partners, social networks, service
              providers, business partners, and data brokers.
            </li>
            <li className="mb-2">
              (b){' '}
              <span className="font-bold">
                Disclosure of Personal Information:
              </span>{' '}
              We disclose the following categories of personal information to our
              service providers for business purposes: Identifiers/biographical
              information, characteristics of protected classifications under
              California or federal law (i.e., age), commercial information,
              payment card information about you, internet or other electronic
              network activity information, and geolocation information. To learn
              more, please see Section 3, Disclosure of Your Information, above.
            </li>
            <li className="mb-2">
              (c){' '}
              <span className="font-bold">
                “Sale” or “Sharing” of Personal Information:
              </span>{' '}
              We may “sell” or “share” (as defined in the CCPA) the following
              categories of personal information to advertising partners to
              facilitate online advertising: Identifiers/biographical information,
              commercial information, internet or other electronic network
              activity information, and inferences drawn from the above.
            </li>
          </ul>
          <p className="uppercase"> 13.6 Retention.</p>
          <p>
            {' '}
            To determine the appropriate retention period for personal
            information, we consider: (i) the amount, nature, and sensitivity of
            the personal information; (ii) the potential risk of harm from
            unauthorized use or disclosure of your personal information; (iii) the
            purposes for which we process your personal information; and (iv)
            applicable legal requirements.
          </p>
          <p className="uppercase"> 14. Changing and Deleting</p>
          <p>
            You may access Information You Share through your profile on our Sites
            and delete, change, or modify certain information. For additional
            assistance with modifying, correcting or deleting information provided
            to our Sites, contact us at{' '}
            <span className="font-bold">
              <a href="mailto:Care@DailyPolished.com">Care@DailyPolished.com.</a>
            </span>
          </p>
          <p className="uppercase"> 15. Questions or Complaints</p>
          <p>
            Should you have any questions or complaints regarding our Privacy
            Policy, please feel free to contact us at{' '}
            <span className="font-bold">
              <a href="mailto:Care@DailyPolished.com">Care@DailyPolished.com.</a>
            </span>
          </p>
          <p>
            If we cannot resolve your question or complaint to your satisfaction,
            you have the right to file a complaint with the competent data
            protection Supervisory Authority in your jurisdiction.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export const getServerSideProps = withServerSideProps(async function () {
  return { props: {} }
}, Pages.PRIVACY_POLICY)

export default PrivacyPolicyPage;
